import React, { useEffect, useMemo, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { formatMngHours, shiftWorkedHours } from "./mngHours";

const CHART_COLORS = [
  "#2563eb",
  "#dc2626",
  "#16a34a",
  "#9333ea",
  "#ea580c",
  "#0891b2",
  "#be185d",
  "#ca8a04",
  "#4f46e5",
  "#059669",
  "#c2410c",
  "#7c3aed",
  "#0d9488",
  "#db2777",
  "#65a30d",
];

const formatHours = (value) =>
  typeof value === "number" ? formatMngHours(value, 2) : value;

const ChartTooltip = ({ active, payload, label, employeeNames }) => {
  if (!active || !payload?.length) {
    return null;
  }

  const rows = payload.filter((entry) => Number(entry.value) > 0);
  if (!rows.length) {
    return null;
  }

  return (
    <div className="z-report-chart-tooltip">
      <p className="z-report-chart-tooltip-date">{label}</p>
      {rows.map((entry) => (
        <p
          key={entry.dataKey}
          className="z-report-chart-tooltip-row"
          style={{ color: entry.color }}
        >
          <span>{employeeNames[entry.dataKey]}</span>
          <strong>{formatHours(entry.value)} ч</strong>
        </p>
      ))}
    </div>
  );
};

const ScheduleMNGChart = ({ schedule, selectedDate }) => {
  const employees = schedule?.table || [];

  const [visibleIds, setVisibleIds] = useState({});

  useEffect(() => {
    if (!employees.length) {
      setVisibleIds({});
      return;
    }
    setVisibleIds((prev) => {
      const next = { ...prev };
      employees.forEach((employee) => {
        if (next[employee.user_id] === undefined) {
          next[employee.user_id] = true;
        }
      });
      return next;
    });
  }, [schedule]);

  const { chartData, employeeNames, chartRevision } = useMemo(() => {
    if (!employees.length || !selectedDate) {
      return { chartData: [], employeeNames: {}, chartRevision: 0 };
    }

    const selectedDateDate = new Date(selectedDate);
    const year = selectedDateDate.getFullYear();
    const month = selectedDateDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const names = {};
    let revision = 0;

    employees.forEach((employee) => {
      names[employee.user_id] = `${employee.surname} ${employee.name}`;
    });

    const data = [];
    for (let day = 1; day <= daysInMonth; day += 1) {
      const row = {
        date: String(day).padStart(2, "0"),
        dateLabel: `${String(day).padStart(2, "0")}.${String(month + 1).padStart(2, "0")}`,
      };
      employees.forEach((employee) => {
        const shift = employee.shifts?.[day - 1];
        const hours = shiftWorkedHours(shift);
        row[employee.user_id] = hours;
        revision += hours * 1000 + (shift?.id ?? 0);
      });
      data.push(row);
    }

    return { chartData: data, employeeNames: names, chartRevision: revision };
  }, [schedule, selectedDate]);

  const visibleEmployees = employees.filter(
    (employee) => visibleIds[employee.user_id],
  );

  const toggleEmployee = (userId) => {
    setVisibleIds((prev) => ({
      ...prev,
      [userId]: !prev[userId],
    }));
  };

  const setAllVisible = (value) => {
    const next = {};
    employees.forEach((employee) => {
      next[employee.user_id] = value;
    });
    setVisibleIds(next);
  };

  if (!employees.length) {
    return null;
  }

  return (
    <div className="z-report-chart-section mt-4">
      <div className="z-report-chart-header">
        <h4 className="z-report-chart-title">График часов по дням месяца</h4>
        <div className="z-report-chart-actions">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setAllVisible(true)}
          >
            Включить всех
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setAllVisible(false)}
          >
            Выключить всех
          </button>
        </div>
      </div>

      <div className="z-report-chart-filters">
        {employees.map((employee, index) => {
          const color = CHART_COLORS[index % CHART_COLORS.length];
          const isVisible = visibleIds[employee.user_id];
          return (
            <label
              key={employee.user_id}
              className={`z-report-chart-filter ${isVisible ? "is-active" : ""}`}
              style={{ "--filter-color": color }}
            >
              <input
                type="checkbox"
                checked={Boolean(isVisible)}
                onChange={() => toggleEmployee(employee.user_id)}
              />
              <span className="z-report-chart-filter-dot" />
              <span className="z-report-chart-filter-name">
                {employee.surname} {employee.name}
              </span>
            </label>
          );
        })}
      </div>

      {visibleEmployees.length === 0 ? (
        <p className="z-report-chart-empty">
          Выберите хотя бы одного сотрудника для графика
        </p>
      ) : (
        <div className="z-report-chart-wrap">
          <ResponsiveContainer width="100%" height={360}>
            <LineChart
              key={chartRevision}
              data={chartData}
              margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#dbe4f0" />
              <XAxis
                dataKey="dateLabel"
                tick={{ fontSize: 11 }}
                interval="preserveStartEnd"
                minTickGap={24}
              />
              <YAxis
                tick={{ fontSize: 11 }}
                tickFormatter={formatHours}
                width={48}
                label={{
                  value: "Часы",
                  angle: -90,
                  position: "insideLeft",
                  style: { fontSize: 11, fill: "#64748b" },
                }}
              />
              <Tooltip
                content={<ChartTooltip employeeNames={employeeNames} />}
              />
              {visibleEmployees.map((employee) => {
                const colorIndex = employees.findIndex(
                  (item) => item.user_id === employee.user_id,
                );
                return (
                  <Line
                    key={employee.user_id}
                    type="monotone"
                    dataKey={employee.user_id}
                    name={String(employee.user_id)}
                    stroke={CHART_COLORS[colorIndex % CHART_COLORS.length]}
                    strokeWidth={2}
                    dot={{ r: 2 }}
                    activeDot={{ r: 4 }}
                    connectNulls
                  />
                );
              })}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default ScheduleMNGChart;
